import { useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { recordSendUpload, recordSendPaste } from '../lib/api';
import type { RecordSendResult } from '../types';

interface Props {
  onClose(): void;
  /** Called after a successful record so callers can invalidate row/batch queries. */
  onRecorded(): void;
}

/**
 * Records a send to Finance — the master source of truth of what was shared.
 * Two ways in, one recording core:
 *   - Upload the exact .xlsx that was emailed (captures manual edits; enables
 *     changed-since-sent detection via per-row snapshots)
 *   - Paste session IDs (e.g. the ones Ambuj confirmed loading into NetSuite)
 */
export function RecordSendModal({ onClose, onRecorded }: Props): JSX.Element {
  const [tab, setTab] = useState<'upload' | 'paste'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [note, setNote] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecordSendResult | null>(null);

  const pastedIds = useMemo(
    () => [...new Set(pasteText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))],
    [pasteText],
  );

  const canSubmit = !running && (tab === 'upload' ? file !== null : pastedIds.length > 0);

  async function submit() {
    setRunning(true);
    setError(null);
    try {
      const res =
        tab === 'upload'
          ? await recordSendUpload(file!, note || undefined)
          : await recordSendPaste(pastedIds, note || undefined);
      setResult(res);
      onRecorded();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  if (result) {
    const { counts } = result;
    return (
      <Modal onClose={onClose}>
        <div className="modal-hd">
          <div className="eb">Sent to finance</div>
          <div className="ttl">Send recorded</div>
        </div>
        <div className="modal-bd">
          <div className="banner ok">
            <span className="dot" />
            <span>
              <strong>{counts.total.toLocaleString()}</strong> session ID{counts.total === 1 ? '' : 's'} recorded ·{' '}
              {counts.newSessions.toLocaleString()} new · {counts.previouslySent.toLocaleString()} previously sent
            </span>
          </div>
          {counts.notFoundInReports > 0 && (
            <div className="banner warn">
              <span className="dot" />
              <span>
                {counts.notFoundInReports.toLocaleString()} ID{counts.notFoundInReports === 1 ? '' : 's'} not in any
                cached report — recorded anyway (expected when backfilling files that predate this dashboard).
              </span>
            </div>
          )}
          {(result.skippedRowNumbers?.length ?? 0) > 0 && (
            <div className="banner warn">
              <span className="dot" />
              <span>
                Skipped {result.skippedRowNumbers!.length} row{result.skippedRowNumbers!.length === 1 ? '' : 's'} with a
                blank session ID (file row{result.skippedRowNumbers!.length === 1 ? '' : 's'}{' '}
                {result.skippedRowNumbers!.slice(0, 12).join(', ')}
                {result.skippedRowNumbers!.length > 12 ? ', …' : ''}).
              </span>
            </div>
          )}
          {(result.duplicateSessionIds?.length ?? 0) > 0 && (
            <div className="d-meta" style={{ fontSize: 12 }}>
              {result.duplicateSessionIds!.length} duplicate ID{result.duplicateSessionIds!.length === 1 ? '' : 's'} in
              the file — counted once each (last row wins for change detection).
            </div>
          )}
          <p style={{ fontSize: 13, color: 'var(--mute)', lineHeight: 1.5, margin: 0 }}>
            Row badges across all reports now reflect this send. Undo from the Sent to finance page if this was a
            mistake.
          </p>
        </div>
        <div className="modal-foot">
          <Button variant="primary" onClick={onClose}>Done</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <div className="modal-hd">
        <div className="eb">Sent to finance</div>
        <div className="ttl">Record a send to finance</div>
      </div>
      <div className="modal-bd">
        {error && (
          <div className="banner err">
            <span className="dot" />
            <span>{error}</span>
          </div>
        )}
        <div>
          <label className="input-label">How are you recording it?</label>
          <div className="segmented">
            <button className={tab === 'upload' ? 'on' : ''} onClick={() => setTab('upload')}>
              Upload the sent file
            </button>
            <button className={tab === 'paste' ? 'on' : ''} onClick={() => setTab('paste')}>
              Paste session IDs
            </button>
          </div>
        </div>

        {tab === 'upload' ? (
          <div>
            <label className="input-label" htmlFor="send-file">.xlsx emailed to finance</label>
            <input
              id="send-file"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="input"
              style={{ height: 'auto', padding: '7px 10px' }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <div className="d-meta" style={{ marginTop: 8, fontSize: 12 }}>
              Upload the exact file that was emailed — including any manual edits — so the record matches what finance
              actually received. Session IDs are matched by the “Coaching.com Session ID” column.
            </div>
          </div>
        ) : (
          <div>
            <label className="input-label" htmlFor="send-ids">Session IDs (one per line, or comma-separated)</label>
            <textarea
              id="send-ids"
              className="input"
              style={{ height: 120, padding: '8px 10px', resize: 'vertical', fontFamily: 'var(--mono, monospace)', fontSize: 12 }}
              placeholder={'8343334\n8426167\n…'}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <div className="d-meta" style={{ marginTop: 8, fontSize: 12 }}>
              {pastedIds.length > 0 ? (
                <>
                  <span className="d-mono" style={{ color: 'var(--ink)' }}>{pastedIds.length}</span> unique ID
                  {pastedIds.length === 1 ? '' : 's'} ready to record.
                </>
              ) : (
                'Use this for confirmations from finance (e.g. the IDs Ambuj actually loaded into NetSuite). No file, so changed-since-sent detection won’t apply to these.'
              )}
            </div>
          </div>
        )}

        <div>
          <label className="input-label" htmlFor="send-note">Note <span style={{ color: 'var(--mute)' }}>· optional</span></label>
          <input
            id="send-note"
            type="text"
            className="input"
            placeholder="e.g. Weekly send to Ambuj"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
      <div className="modal-foot">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!canSubmit} onClick={submit}>
          {running ? <><span className="spinner" />Recording…</> : 'Record send'}
        </Button>
      </div>
    </Modal>
  );
}
